import { Component, Input, Output, EventEmitter, OnInit, forwardRef, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { AppConfigService } from '../../appconfig/appconfig.service';
import { Observable } from 'rxjs';

export interface ImageUploadConfig {
  uploadUrl: string;
  maxSizeBytes?: number;
  allowedTypes?: string[];
  minWidth?: number;
  minHeight?: number;
  requireSquare?: boolean;
  uploadButtonText?: string;
  dropText?: string;
  requirementsText?: string;
}

export interface ImageUploadResult {
  url: string;
  originalUrl?: string;
  iconUrl?: string;
}

@Component({
  standalone: true,
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  imports: [CommonModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploadComponent),
      multi: true
    }
  ]
})
export class ImageUploadComponent implements OnInit, ControlValueAccessor {
  @Input() config: ImageUploadConfig;
  @Input() currentImageUrl: string = ''; // Deprecated - kept for backwards compatibility
  @Input() disabled: boolean = false;
  @Input() previewSize: number = 150;
  @Output() uploadComplete = new EventEmitter<ImageUploadResult>();
  @Output() uploadError = new EventEmitter<string>();

  public uploading: boolean = false;
  public uploadProgress: number = 0;
  public dragOver: boolean = false;
  public imageTimestamp: number = Date.now();
  public readonly inputId: string = `imageFileInput_${Math.random().toString(36).slice(2)}`;

  // ControlValueAccessor implementation
  private _value: string = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  private defaultConfig: Partial<ImageUploadConfig> = {
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
    minWidth: 64,
    minHeight: 64,
    requireSquare: false,
    uploadButtonText: 'Upload Image',
    dropText: 'Drop your image here or click to browse files',
    requirementsText: 'PNG, JPEG, or GIF format'
  };

  constructor(
    private http: HttpClient,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.config = { ...this.defaultConfig, ...this.config };
    // Support backwards compatibility with currentImageUrl input
    if (this.currentImageUrl && !this._value) {
      this._value = this.currentImageUrl;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this._value = value || '';
    this.imageTimestamp = Date.now();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get value(): string {
    // Support backwards compatibility - use currentImageUrl if value not set
    return this._value || this.currentImageUrl || '';
  }

  onDragOver(event: DragEvent) {
    if (this.disabled || this.uploading) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent) {
    if (this.disabled || this.uploading) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files[0]);
    }
  }

  onFileSelected(event: Event) {
    if (this.disabled || this.uploading) return;
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.handleFileUpload(target.files[0]);
    }
  }

  triggerFileInput() {
    if (this.disabled || this.uploading) return;
    const input = document.getElementById(this.inputId) as HTMLInputElement;
    input?.click();
  }

  private handleFileUpload(file: File) {
    // Validate file type
    if (!this.config.allowedTypes.includes(file.type)) {
      const error = `Only ${this.config.allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')} files are allowed`;
      this.toastr.error(error, 'Invalid File Type');
      this.uploadError.emit(error);
      return;
    }

    // Validate file size
    if (file.size > this.config.maxSizeBytes) {
      const error = `File size must be less than ${Math.round(this.config.maxSizeBytes / 1024 / 1024)}MB`;
      this.toastr.error(error, 'File Too Large');
      this.uploadError.emit(error);
      return;
    }

    // Validate image dimensions if required
    this.validateImageDimensions(file).subscribe({
      next: (isValid) => {
        if (!isValid) {
          let error = `Image must be at least ${this.config.minWidth}x${this.config.minHeight} pixels`;
          if (this.config.requireSquare) {
            error = `Image must be square and at least ${this.config.minWidth}x${this.config.minHeight} pixels`;
          }
          this.toastr.error(error, 'Invalid Dimensions');
          this.uploadError.emit(error);
          return;
        }

        this.uploadImage(file);
      },
      error: () => {
        this.toastr.error('Error validating image', 'Validation Failed');
        this.uploadError.emit('Error validating image');
      }
    });
  }

  private validateImageDimensions(file: File): Observable<boolean> {
    return new Observable(observer => {
      const img = new Image();
      img.onload = () => {
        const meetsMinSize = img.width >= this.config.minWidth && img.height >= this.config.minHeight;
        const isSquare = !this.config.requireSquare || img.width === img.height;
        observer.next(meetsMinSize && isSquare);
        observer.complete();
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        observer.error('Failed to load image');
      };
      img.src = URL.createObjectURL(file);
    });
  }

  private uploadImage(file: File) {
    this.uploading = true;
    this.uploadProgress = 0;

    const formData = new FormData();
    formData.append('file', file);

    const apiUrl = AppConfigService.settings.get('chatstyleapiServer');
    const uploadUrl = `${apiUrl}${this.config.uploadUrl}`;

    this.http.post<any>(uploadUrl, formData, {
      reportProgress: true,
      observe: 'events'
    }).subscribe({
      next: (event) => {
        if (event.type === 1) { // UploadProgress
          this.uploadProgress = Math.round(100 * event.loaded / event.total);
        } else if (event.type === 4) { // Response
          this.uploading = false;
          this.uploadProgress = 0;

          const baseUrl = apiUrl.endsWith('/') ? apiUrl : apiUrl + '/';

          // Handle different response formats
          let result: ImageUploadResult;
          if (event.body.profilePicUrl) {
            // Audience profile upload response
            result = {
              url: baseUrl + event.body.profilePicUrl
            };
          } else if (event.body.avatarUrl) {
            // Bot image upload response
            result = {
              url: baseUrl + event.body.avatarUrl,
              originalUrl: baseUrl + event.body.originalUrl,
              iconUrl: event.body.iconUrl ? baseUrl + event.body.iconUrl : undefined
            };
          } else {
            // Generic response
            result = {
              url: baseUrl + event.body.url
            };
          }

          // Update ControlValueAccessor value
          this._value = result.url;
          this.onChange(result.url); // Notify form control
          this.onTouched(); // Mark as touched

          // Backwards compatibility - update currentImageUrl
          this.currentImageUrl = result.url;
          this.imageTimestamp = Date.now(); // Update timestamp to force image refresh

          // Emit events for backwards compatibility and advanced use cases
          this.uploadComplete.emit(result);
          this.toastr.success('Image uploaded successfully', 'Upload Complete');
        }
      },
      error: (error) => {
        this.uploading = false;
        this.uploadProgress = 0;
        
        let errorMessage = 'Failed to upload image';
        if (error.status === 0) {
          errorMessage = 'Cannot connect to server. Please check if the API is running.';
        } else if (error.status === 404) {
          errorMessage = 'Upload endpoint not found. Please check the API configuration.';
        } else if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.toastr.error(errorMessage, 'Upload Failed');
        this.uploadError.emit(errorMessage);
      }
    });
  }
}